// src/components/admin/SalesReport.js
import { Calendar, DollarSign, Download, Package, ShoppingBag } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import axios from '../../utils/axiosHandler';

const SalesReport = () => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchReport();
  }, [dateRange]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `/api/admin/reports/sales?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      const data = await response.data;
      setReportData(data);
    } catch (error) {
      console.error('Error fetching sales report:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async () => {
    try {
      setDownloadLoading(true);
  
      const token = localStorage.getItem('token');
  
      const response = await axios.get(
        `/api/admin/reports/sales/export`,
        {
          params: {
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: 'blob',  // crucial for file download
        }
      );
  
      // Optional: Try to get filename from header if server provides it
      const contentDisposition = response.headers['content-disposition'];
      let filename = `sales-report-${dateRange.startDate}-to-${dateRange.endDate}.xlsx`;
  
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
  
      // Create a temporary URL for the blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
  
      // Create a temporary link element
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
  
      // Trigger the download
      document.body.appendChild(a);
      a.click();
  
      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
  
    } catch (error) {
      console.error('Error downloading report:', error);
      alert('Failed to download report. Please try again.');
    } finally {
      setDownloadLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">

    {/* Header */}
      <h2 className="text-2xl font-semibold">Security Dashboard</h2>

    <div className="p-6 space-y-6 bg-gray-50 rounded-xl">
      <div className="flex justify-between items-center">
        <div className="flex gap-4">
          <div className="flex items-center space-x-4">
            <div>
              <label className="block text-sm text-gray-600">Start Date</label>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                className="border rounded p-2"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600">End Date</label>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                className="border rounded p-2"
              />
            </div>
          </div>
          <div className="flex">
            <button
                onClick={downloadReport}
                disabled={downloadLoading}
                className={`flex items-center mt-4 ${
                    downloadLoading ? 'bg-blue-300' : 'bg-blue-500 hover:bg-blue-600'
                } text-white rounded-lg`}
                >
                {downloadLoading ? (
                    <>
                    <div className="animate-spin h-5 w-5 mr-2 border-2 border-white border-t-transparent rounded-full" />
                    { window.innerWidth >= 768  ? (
                        <span className='p-0 m-0'>Downloading...</span>): 
                        ( <span className='p-0 m-0'></span>)
                     }    
                    </>
                ) : (
                    <>
                    <Download className="h-5 w-5" />
                     { (window.innerWidth >= 768) && 
                        <span className='p-0 m-0 ml-2'>Download Report</span>
                     }                  
                    </>
                )}
            </button>
          </div>
          
        </div>
      </div>

      {reportData && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Revenue</p>
                  <p className="text-2xl font-bold"> 
                    ${reportData.totalRevenue > 0 ? (reportData?.totalRevenue.toFixed(2)) : ("0")}
                    
                   </p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <DollarSign className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Orders</p>
                  <p className="text-2xl font-bold">{reportData.totalOrders}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <ShoppingBag className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Products Sold</p>
                  <p className="text-2xl font-bold">{reportData.productsSold}</p>
                </div>
                <div className="p-3 bg-purple-100 rounded-full">
                  <Package className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Avg. Order Value</p>
                  <p className="text-2xl font-bold">
                  ${reportData.averageOrderValue > 0 ? (reportData?.averageOrderValue.toFixed(2)) : ("0")}

                   </p>
                </div>
                <div className="p-3 bg-orange-100 rounded-full">
                  <Calendar className="h-6 w-6 text-orange-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Revenue Chart */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-4">Daily Revenue</h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={Object.entries(reportData.dailyRevenue).map(([date, revenue]) => ({
                    date,
                    revenue
                  }))}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(value) => new Date(value).toLocaleDateString()}
                  />
                  <YAxis />
                  <Tooltip 
                    formatter={(value) => [`${value.toFixed(2)}`, 'Revenue']}
                    labelFormatter={(value) => new Date(value).toLocaleDateString()}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="revenue" 
                    stroke="#0088FE" 
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Top Products */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-4">Top Products</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Product
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quantity Sold
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Base Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Revenue
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reportData.topProducts.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {item.product.name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{item.quantity}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          ${item.product.basePrice.toFixed(2)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          ${(item.quantity * item.product.basePrice).toFixed(2)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Order Status */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-4">Orders by Status</h3>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {Object.entries(reportData.ordersByStatus).map(([status, count]) => (
                <div key={status} className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-500 capitalize">{status}</div>
                  <div className="text-2xl font-bold">{count}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default SalesReport;



// // src/components/admin/SalesReport.js
// import React, { useState, useEffect } from 'react';
// import { Download, Calendar, DollarSign, Package, ShoppingBag } from 'lucide-react';
// import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';

// const SalesReport = () => {
//   const [reportData, setReportData] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [downloadLoading, setDownloadLoading] = useState(false);
//   const [dateRange, setDateRange] = useState({
//     startDate: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
//     endDate: new Date().toISOString().split('T')[0]
//   });

//   useEffect(() => {
//     fetchReport();
//   }, [dateRange]);

//   const fetchReport = async () => {
//     try {
//       setLoading(true);
//       const token = localStorage.getItem('token');
//       const response = await fetch(
//         `/api/admin/reports/sales?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`,
//         {
//           headers: { Authorization: `Bearer ${token}` }
//         }
//       );
//       const data = await response.json();
//       setReportData(data);
//     } catch (error) {
//       console.error('Error fetching sales report:', error);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const downloadReport = async () => {
//     try {
//       setDownloadLoading(true);
//       const token = localStorage.getItem('token');
      
//       // Create a fetch request instead of window.open to handle the download properly
//       const response = await fetch(
//         `/api/admin/reports/sales/export?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`,
//         {
//           headers: { 
//             Authorization: `Bearer ${token}` 
//           }
//         }
//       );
      
//       if (!response.ok) {
//         throw new Error('Failed to download report');
//       }
      
//       // Get the blob from the response
//       const blob = await response.blob();
      
//       // Create a temporary URL for the blob
//       const url = window.URL.createObjectURL(blob);
      
//       // Create a temporary link element
//       const a = document.createElement('a');
//       a.href = url;
//       a.download = `sales-report-${dateRange.startDate}-to-${dateRange.endDate}.xlsx`;
      
//       // Trigger the download
//       document.body.appendChild(a);
//       a.click();
      
//       // Clean up
//       window.URL.revokeObjectURL(url);
//       document.body.removeChild(a);
//     } catch (error) {
//       console.error('Error downloading report:', error);
//       alert('Failed to download report. Please try again.');
//     } finally {
//       setDownloadLoading(false);
//     }
//   };

//   if (loading) {
//     return (
//       <div className="flex justify-center items-center h-64">
//         <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
//       </div>
//     );
//   }

//   return (
//     <div className="space-y-6">

//     {/* Header */}
//       <h2 className="text-2xl font-semibold">Security Dashboard</h2>

//     <div className="p-6 space-y-6 bg-gray-50 rounded-xl">
//       <div className="flex justify-between items-center">
//       <p className="font-semibold text-gray-600">Monitor your sales events</p>
//         <div className="flex gap-4">
//           <div className="flex items-center space-x-4">
//             <div>
//               <label className="block text-sm text-gray-600">Start Date</label>
//               <input
//                 type="date"
//                 value={dateRange.startDate}
//                 onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
//                 className="border rounded p-2"
//               />
//             </div>
//             <div>
//               <label className="block text-sm text-gray-600">End Date</label>
//               <input
//                 type="date"
//                 value={dateRange.endDate}
//                 onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
//                 className="border rounded p-2"
//               />
//             </div>
//           </div>
//           <button
//             onClick={downloadReport}
//             disabled={downloadLoading}
//             className={`flex items-center mt-4 px-4 py-2 ${
//                 downloadLoading ? 'bg-blue-300' : 'bg-blue-500 hover:bg-blue-600'
//             } text-white rounded-lg`}
//             >
//             {downloadLoading ? (
//                 <>
//                 <div className="animate-spin h-5 w-5 mr-2 border-2 border-white border-t-transparent rounded-full" />
//                 Downloading...
//                 </>
//             ) : (
//                 <>
//                 <Download className="h-5 w-5 mr-2" />
//                 Download Excel Report
//                 </>
//             )}
//           </button>
//         </div>
//       </div>

//       {reportData && (
//         <div className="space-y-6">
//           {/* Summary Cards */}
//           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
//             <div className="bg-white p-6 rounded-lg shadow">
//               <div className="flex items-center justify-between">
//                 <div>
//                   <p className="text-sm text-gray-500">Total Revenue</p>
//                   <p className="text-2xl font-bold"> 
//                     ${reportData.totalRevenue > 0 ? (reportData?.totalRevenue.toFixed(2)) : ("0")}
                    
//                    </p>
//                 </div>
//                 <div className="p-3 bg-green-100 rounded-full">
//                   <DollarSign className="h-6 w-6 text-green-600" />
//                 </div>
//               </div>
//             </div>

//             <div className="bg-white p-6 rounded-lg shadow">
//               <div className="flex items-center justify-between">
//                 <div>
//                   <p className="text-sm text-gray-500">Total Orders</p>
//                   <p className="text-2xl font-bold">{reportData.totalOrders}</p>
//                 </div>
//                 <div className="p-3 bg-blue-100 rounded-full">
//                   <ShoppingBag className="h-6 w-6 text-blue-600" />
//                 </div>
//               </div>
//             </div>

//             <div className="bg-white p-6 rounded-lg shadow">
//               <div className="flex items-center justify-between">
//                 <div>
//                   <p className="text-sm text-gray-500">Products Sold</p>
//                   <p className="text-2xl font-bold">{reportData.productsSold}</p>
//                 </div>
//                 <div className="p-3 bg-purple-100 rounded-full">
//                   <Package className="h-6 w-6 text-purple-600" />
//                 </div>
//               </div>
//             </div>

//             <div className="bg-white p-6 rounded-lg shadow">
//               <div className="flex items-center justify-between">
//                 <div>
//                   <p className="text-sm text-gray-500">Avg. Order Value</p>
//                   <p className="text-2xl font-bold">
//                   ${reportData.averageOrderValue > 0 ? (reportData?.averageOrderValue.toFixed(2)) : ("0")}

//                    </p>
//                 </div>
//                 <div className="p-3 bg-orange-100 rounded-full">
//                   <Calendar className="h-6 w-6 text-orange-600" />
//                 </div>
//               </div>
//             </div>
//           </div>

//           {/* Revenue Chart */}
//           <div className="bg-white p-6 rounded-lg shadow">
//             <h3 className="text-lg font-semibold mb-4">Daily Revenue</h3>
//             <div className="h-80">
//               <ResponsiveContainer width="100%" height="100%">
//                 <LineChart
//                   data={Object.entries(reportData.dailyRevenue).map(([date, revenue]) => ({
//                     date,
//                     revenue
//                   }))}
//                   margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
//                 >
//                   <CartesianGrid strokeDasharray="3 3" />
//                   <XAxis 
//                     dataKey="date" 
//                     tickFormatter={(value) => new Date(value).toLocaleDateString()}
//                   />
//                   <YAxis />
//                   <Tooltip 
//                     formatter={(value) => [`${value.toFixed(2)}`, 'Revenue']}
//                     labelFormatter={(value) => new Date(value).toLocaleDateString()}
//                   />
//                   <Legend />
//                   <Line 
//                     type="monotone" 
//                     dataKey="revenue" 
//                     stroke="#0088FE" 
//                     strokeWidth={2}
//                     dot={{ r: 4 }}
//                     activeDot={{ r: 6 }}
//                   />
//                 </LineChart>
//               </ResponsiveContainer>
//             </div>
//           </div>

//           {/* Top Products */}
//           <div className="bg-white p-6 rounded-lg shadow">
//             <h3 className="text-lg font-semibold mb-4">Top Products</h3>
//             <div className="overflow-x-auto">
//               <table className="min-w-full divide-y divide-gray-200">
//                 <thead>
//                   <tr>
//                     <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                       Product
//                     </th>
//                     <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                       Quantity Sold
//                     </th>
//                     <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                       Base Price
//                     </th>
//                     <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                       Total Revenue
//                     </th>
//                   </tr>
//                 </thead>
//                 <tbody className="bg-white divide-y divide-gray-200">
//                   {reportData.topProducts.map((item, index) => (
//                     <tr key={index} className="hover:bg-gray-50">
//                       <td className="px-6 py-4 whitespace-nowrap">
//                         <div className="text-sm font-medium text-gray-900">
//                           {item.product.name}
//                         </div>
//                       </td>
//                       <td className="px-6 py-4 whitespace-nowrap">
//                         <div className="text-sm text-gray-900">{item.quantity}</div>
//                       </td>
//                       <td className="px-6 py-4 whitespace-nowrap">
//                         <div className="text-sm text-gray-900">
//                           ${item.product.basePrice.toFixed(2)}
//                         </div>
//                       </td>
//                       <td className="px-6 py-4 whitespace-nowrap">
//                         <div className="text-sm text-gray-900">
//                           ${(item.quantity * item.product.basePrice).toFixed(2)}
//                         </div>
//                       </td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             </div>
//           </div>

//           {/* Order Status */}
//           <div className="bg-white p-6 rounded-lg shadow">
//             <h3 className="text-lg font-semibold mb-4">Orders by Status</h3>
//             <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
//               {Object.entries(reportData.ordersByStatus).map(([status, count]) => (
//                 <div key={status} className="bg-gray-50 p-4 rounded-lg">
//                   <div className="text-sm text-gray-500 capitalize">{status}</div>
//                   <div className="text-2xl font-bold">{count}</div>
//                 </div>
//               ))}
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//     </div>
//   );
// };

// export default SalesReport;